/**
 * Job Sites auth summary — READ-ONLY, flag-gated summary card.
 *
 * Sources GET /job-connections/site-status-board (JWT), the read-only board
 * behind the backend flag SAIK_SITE_STATUS_BOARD_ENABLED. While the flag is OFF
 * the endpoint answers {enabled:false} and this card renders NOTHING, so the
 * component is INERT in production. It also renders nothing if the request
 * errors or times out. When enabled:true it shows two counts from the
 * response: sites ready to apply (utilizable) and sites needing sign-in /
 * verification (needs_verification).
 */
import { useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import Card from './Card';
import { jobbotApi, type SiteStatusBoardCounts } from '../api/jobbot';

/** Keep a slow/dead backend from ever leaving a stray placeholder on screen. */
const REQUEST_TIMEOUT_MS = 8000;

export default function SiteAuthSummaryCard() {
  const [counts, setCounts] = useState<SiteStatusBoardCounts | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    (async () => {
      try {
        const res = await jobbotApi.extension.siteStatusBoard({ signal: controller.signal });
        const data = res.data;
        if (!alive) return;
        // Flag OFF → endpoint returns enabled:false → render nothing (inert).
        setCounts(data?.enabled ? data.counts : null);
      } catch {
        // Network error / timeout / disabled → render nothing.
        if (alive) setCounts(null);
      } finally {
        window.clearTimeout(timer);
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
      controller.abort();
      window.clearTimeout(timer);
    };
  }, []);

  if (loading) return null; // never flash a placeholder before we know the flag
  if (!counts) return null; // flag OFF / error / timeout → inert

  return (
    <Card className="mb-6 p-4" data-agent-id="jobbot.sites.auth-summary">
      <div className="mb-3 flex items-center gap-2">
        <ShieldCheck size={18} className="text-brand-600" aria-hidden="true" />
        <h2 className="font-semibold text-slate-950 dark:text-slate-100">Job Sites auth summary</h2>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3" data-agent-id="jobbot.sites.auth-summary.utilizable">
          <p className="text-2xl font-semibold text-green-700 dark:text-green-400">{counts.utilizable}</p>
          <p className="text-sm text-slate-600 dark:text-slate-400">sites ready to apply</p>
        </div>
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3" data-agent-id="jobbot.sites.auth-summary.needs-verification">
          <p className="text-2xl font-semibold text-amber-700 dark:text-amber-400">{counts.needs_verification}</p>
          <p className="text-sm text-slate-600 dark:text-slate-400">need sign-in / verification</p>
        </div>
      </div>
    </Card>
  );
}
